import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { ICategory } from '../../interfaces/category';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import { useNavigate } from 'react-router-dom';

const Category: React.FC = () => {
	const [categories, setCategories] = useState<ICategory[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const navigate = useNavigate();

	const { showModal, modalProps, confirmAction } = useConfirmationModal();

	useEffect(() => {
		getCategories();
	}, []);

	const getCategories = async () => {
		try {
			setLoading(true);
			const response = await callApi(API_URL_2 + API_ENDPOINT.page + 'categories', { method: 'GET' });
			setCategories(response);
		} catch (error: any) {
			console.error('Failed to fetch categories:', error.message);
		} finally {
			setLoading(false);
		}
	};

	const deleteCategory = async (id: string) => {
		try {
			await callApi(API_URL_2 + API_ENDPOINT.page + `categories/${id}`, { method: 'DELETE' });
			setCategories((prevCategories: ICategory[]) => prevCategories.filter((cat) => cat.id !== id));
		} catch (error: any) {
			console.error('Failed to delete category:', error.message);
		}
	};

	const handleEditCategory = (category: ICategory) => {
		navigate(`/categories/edit/${category.id}`);
	};

	const handleAddSubcategory = (category: ICategory) => {
		navigate(`/categories/add?parentId=${category.id}&type=sub`);
	};

	const handleAddCategory = () => {
		navigate('/categories/add');
	};

	const [openRows, setOpenRows] = useState<Record<string, any>>({});

	const toggleRow = (id: any) => {
		setOpenRows((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
	};

	const handleDeleteClick = (id: string) => {
		confirmAction({
			title: 'Confirm Delete',
			message: 'Are you sure you want to delete this category?',
			onConfirm: () => deleteCategory(id),
		});
	};

	return (
		<Layout>
			<div className="d-flex justify-content-between align-items-center mb-4">
				<h1 className="app-page-title">Categories</h1>
				<button
					className="btn btn-primary"
					onClick={handleAddCategory}
				>
					<i className="bi bi-plus-circle me-2"></i>
					Add Category
				</button>
			</div>

			<div className="row">
				<div className="col-12">
					<div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
						<div className="app-card-body">
							<div className="table-responsive">
								<table className="table app-table-hover mb-0 text-left">
									<thead>
										<tr>
											<th className="cell">S.no.</th>
											<th className="cell">Image</th>
											<th className="cell">Category</th>
											<th className="cell">Type</th>
											<th className="cell">Recommended</th>
											<th className="cell">Actions</th>
										</tr>
									</thead>
									<tbody>
										{loading && (
											<tr>
												<td className="text-center" colSpan={6}>
													Loading categories...
												</td>
											</tr>
										)}

										{categories.length === 0 && !loading ? (
											<tr>
												<td className="text-center" colSpan={6}>
													No categories available to display.
												</td>
											</tr>
										) : (
											categories
												.filter((cat) => cat.type === "primary")
												.map((cat, index) => (
													<React.Fragment key={cat.id}>
														<tr
															className="accordion-toggle"
															onClick={() => toggleRow(cat.id)}
														>
															<td className="cell">
																<button
																	className="btn btn-link"
																	onClick={(e) => {
																		e.stopPropagation();
																		toggleRow(cat.id);
																	}}
																>
																	{openRows[cat.id] ?
																		<i className="fa fa-chevron-up" aria-hidden="true"></i> :
																		<i className="fa fa-chevron-down" aria-hidden="true"></i>
																	}
																</button>
																#{index + 1}
															</td>
															<td className="cell">
																<div className="table-image">
																	<img className="img-image" src={cat.image} alt={cat.title} />
																</div>
															</td>
															<td className="cell">{cat.title}</td>
															<td className="cell">
																<span className="badge bg-primary">Primary</span>
															</td>
															<td className="cell">{cat.isRecommended ? "Yes" : 'No'}</td>
															<td className="cell">
																<div className='d-flex align-items-center gap-1'>
																	<button
																		className="btn px-2 py-1 app-btn-primary align-items-center d-flex gap-1"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleAddSubcategory(cat);
																		}}
																	>
																		<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 14 14" fill="none">
																			<path d="M7 0C7.23206 0 7.45462 0.0921874 7.61872 0.256282C7.78281 0.420376 7.875 0.642936 7.875 0.875V6.125H13.125C13.3571 6.125 13.5796 6.21719 13.7437 6.38128C13.9078 6.54538 14 6.76794 14 7C14 7.23206 13.9078 7.45462 13.7437 7.61872C13.5796 7.78281 13.3571 7.875 13.125 7.875H7.875V13.125C7.875 13.3571 7.78281 13.5796 7.61872 13.7437C7.45462 13.9078 7.23206 14 7 14C6.76794 14 6.54538 13.9078 6.38128 13.7437C6.21719 13.5796 6.125 13.3571 6.125 13.125V7.875H0.875C0.642936 7.875 0.420376 7.78281 0.256282 7.61872C0.0921874 7.45462 0 7.23206 0 7C0 6.76794 0.0921874 6.54538 0.256282 6.38128C0.420376 6.21719 0.642936 6.125 0.875 6.125H6.125V0.875C6.125 0.642936 6.21719 0.420376 6.38128 0.256282C6.54538 0.0921874 6.76794 0 7 0Z" fill="white" />
																		</svg>
																		Add Sub Category
																	</button>
																	<button
																		className="btn btn-success px-2 py-1 text-white"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleEditCategory(cat);
																		}}
																	>
																		<i className="bi bi-pen"></i>
																	</button>
																	<button
																		className="btn btn-danger px-2 py-1 text-white"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleDeleteClick(cat.id);
																		}}
																	>
																		<i className="bi bi-trash3"></i>
																	</button>
																</div>
															</td>
														</tr>
														{openRows[cat.id] && (
															<tr>
																<td colSpan={6}>
																	<div className="accordion-body">
																		<table className="table table-striped">
																			<thead>
																				<tr>
																					<th className="cell">S.no.</th>
																					<th className="cell">Image</th>
																					<th className="cell">Sub Category</th>
																					<th className="cell">Type</th>
																					<th className="cell">Recommended</th>
																					<th className="cell">Actions</th>
																				</tr>
																			</thead>
																			<tbody>
																				{categories
																					.filter((sCat) => sCat.categoryId === cat.id)
																					.map((sCat, idx) => (
																						<tr key={sCat.id}>
																							<td className="cell">#{idx + 1}</td>
																							<td className="cell">
																								<div className="table-image">
																									<img className="img-image" src={sCat.image} alt={sCat.title} />
																								</div>
																							</td>
																							<td className="cell">{sCat.title}</td>
																							<td className="cell">
																								<span className="badge bg-secondary">Sub Category</span>
																							</td>
																							<td className="cell">{sCat.isRecommended ? "Yes" : "No"}</td>
																							<td className="cell">
																								<div className='d-flex align-items-center gap-1'>
																									<button
																										className="btn btn-success px-2 py-1 text-white"
																										onClick={(e) => {
																											e.stopPropagation();
																											handleEditCategory(sCat);
																										}}
																									>
																										<i className="bi bi-pen"></i>
																									</button>
																									<button
																										className="btn btn-danger px-2 py-1 text-white"
																										onClick={(e) => {
																											e.stopPropagation();
																											handleDeleteClick(sCat.id);
																										}}
																									>
																										<i className="bi bi-trash3"></i>
																									</button>
																								</div>
																							</td>
																						</tr>
																					))}
																			</tbody>
																		</table>
																	</div>
																</td>
															</tr>
														)}
													</React.Fragment>
												))
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
			<ConfirmationModal {...modalProps} show={showModal} />
		</Layout>
	);
};

export default Category;